export type BadgeType = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary';

export interface FormattedStatus {
  label: string;
  badgeType: BadgeType;
  dot?: boolean;
  pulse?: boolean;
}

export function getResearchStatusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'in_progress':
    case 'enhancing':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'deleting':
    case 'failed':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

export interface ResearchStatusIconData {
  iconName: 'check-circle' | 'trending-up' | 'alert-triangle' | 'file-text';
  colorClass: string;
}

export function getResearchStatusIconData(status: string): ResearchStatusIconData {
  switch (status?.toLowerCase()) {
    case 'completed':
      return { iconName: 'check-circle', colorClass: 'text-emerald-600' };
    case 'in_progress':
    case 'enhancing':
      return { iconName: 'trending-up', colorClass: 'text-blue-600' };
    case 'deleting':
      return { iconName: 'trending-up', colorClass: 'text-red-600' };
    case 'failed':
      return { iconName: 'alert-triangle', colorClass: 'text-red-600' };
    default:
      return { iconName: 'file-text', colorClass: 'text-slate-500' };
  }
}

export function getContractStatusColor(status: string): string {
  switch (status) {
    case 'COMPLETED':
      return 'text-green-600 bg-green-50';
    case 'IN_PROGRESS':
      return 'text-blue-600 bg-blue-50';
    case 'FAILED':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
}

export function getContractStatusVariant(status: string): 'success' | 'info' | 'error' | 'neutral' {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'IN_PROGRESS':
      return 'info';
    case 'FAILED':
      return 'error';
    default:
      return 'neutral';
  }
}

export const SIGNING_STATUS_MAP: Record<string, { type: BadgeType; label: string }> = {
  pending: { type: 'neutral', label: 'Pending' },
  viewed: { type: 'info', label: 'Viewed' },
  signed: { type: 'success', label: 'Signed' },
  expired: { type: 'warning', label: 'Expired' },
  revoked: { type: 'error', label: 'Revoked' },
};

type CRMStatusDomain = 'priority' | 'contactStatus' | 'emailStatus' | 'enrollmentStatus' | 'taskStatus';

const PRIORITY_MAP: Record<string, FormattedStatus> = {
  high: { label: 'High', badgeType: 'error' },
  medium: { label: 'Medium', badgeType: 'warning' },
  low: { label: 'Low', badgeType: 'success' },
};

const CONTACT_STATUS_MAP: Record<string, FormattedStatus> = {
  lead: { label: 'Lead', badgeType: 'neutral' },
  prospect: { label: 'Prospect', badgeType: 'info' },
  customer: { label: 'Customer', badgeType: 'success' },
};

const EMAIL_STATUS_MAP: Record<string, FormattedStatus> = {
  sent: { label: 'Sent', badgeType: 'warning' },
  pending: { label: 'Pending', badgeType: 'warning' },
  delivered: { label: 'Delivered', badgeType: 'neutral' },
  opened: { label: 'Opened', badgeType: 'info' },
  clicked: { label: 'Clicked', badgeType: 'success' },
  bounced: { label: 'Bounced', badgeType: 'error' },
};

const ENROLLMENT_STATUS_MAP: Record<string, FormattedStatus> = {
  active: { label: 'Active', badgeType: 'success', dot: true, pulse: true },
  paused: { label: 'Paused', badgeType: 'warning' },
  completed: { label: 'Completed', badgeType: 'info' },
  cancelled: { label: 'Cancelled', badgeType: 'neutral' },
};

const TASK_STATUS_MAP: Record<string, FormattedStatus> = {
  pending: { label: 'Pending', badgeType: 'warning' },
  in_progress: { label: 'In Progress', badgeType: 'info', dot: true, pulse: true },
  completed: { label: 'Completed', badgeType: 'success' },
  cancelled: { label: 'Cancelled', badgeType: 'neutral' },
};

const CRM_DOMAIN_MAPS: Record<CRMStatusDomain, Record<string, FormattedStatus>> = {
  priority: PRIORITY_MAP,
  contactStatus: CONTACT_STATUS_MAP,
  emailStatus: EMAIL_STATUS_MAP,
  enrollmentStatus: ENROLLMENT_STATUS_MAP,
  taskStatus: TASK_STATUS_MAP,
};

export function formatCRMStatus(value: string, domain: CRMStatusDomain): FormattedStatus {
  const map = CRM_DOMAIN_MAPS[domain];
  if (map[value]) return map[value];

  return {
    label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '),
    badgeType: 'neutral',
  };
}

export const ACTIVITY_ICONS: Record<string, string> = {
  call: '\u{1F4DE}',
  email: '\u{1F4E7}',
  meeting: '\u{1F4C5}',
  task: '\u{2705}',
  note: '\u{1F4DD}',
  SMS: '\u{1F4AC}',
  LinkedIn: '\u{1F4BC}',
};

export function getCorrelationBadgeType(strength: string): BadgeType {
  switch (strength) {
    case 'HIGH':
      return 'success';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function getPrecedentBadgeType(type: string): BadgeType {
  switch (type) {
    case 'SUPPORTING':
      return 'success';
    case 'ADVERSE':
      return 'error';
    default:
      return 'neutral';
  }
}

export function getMaterialTypeBadgeType(type: string): BadgeType {
  switch (type) {
    case 'case':
      return 'info';
    case 'statute':
      return 'info';
    default:
      return 'neutral';
  }
}
